import { isDateBefore } from 'helpers/timeFormatter';
import {
  InstagramApiType,
  InstagramSocialProfile,
  PostFeedType,
  PostItemFragment,
  PostPublishMethod
} from 'models/graphql';
import { profilesHelpers } from '../../../modules/Settings/Profiles/Profiles.helpers';
import { SocialNetworksEnum } from '../../../constants/social-networks';
import { PostScheduleViewContext } from '../../../modules/Publish/PostSchedule/PostSchedule.controller';
import { PostQueueStatLabelsEnum } from '../../containers/PostQueueStat/PostQueueStat.view';

export type statisticsConditionType = {
  show: boolean;
  isInstagram: boolean;
  isTwitter: boolean;
  hasPreviousPosts: boolean;
  network: SocialNetworksEnum | null;
  message: string | undefined;
};

export type statDetails = { stat: PostQueueStatLabelsEnum; isDisabled: boolean };

type AnalyticsDateLimitType = 'twitter' | 'google' | 'instagram';
enum AnalyticsDateLimitEnum {
  twitter = '2023-04-29',
  google = '2023-02-01',
  instagram = '2025-01-01'
}

const isLinkedInSocialProfile = (obj: unknown): obj is { type: 'company' | 'personal' } => {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'type' in obj &&
    ((obj as { type: string }).type === 'company' || (obj as { type: string }).type === 'personal')
  );
};

const isBeforeDateLimit = (date: string | string[], dateLimitType: AnalyticsDateLimitType) => {
  return isDateBefore(date, new Date(AnalyticsDateLimitEnum[dateLimitType]));
};

const statisticsMessaging = (
  post: PostItemFragment,
  network: SocialNetworksEnum | null,
  context?: PostScheduleViewContext
) => {
  const isInstagramPersonal = isInstagramSocialProfile(post.socialProfile) && post.socialProfile.type === 'personal';

  if (post.socialProfile.__typename === 'GenericSocialProfile') {
    return 'Post stats are not available. You were sent a notification to publish this post manually.';
  }

  switch (network) {
    case SocialNetworksEnum.twitter:
      return isBeforeDateLimit(post.datePosted, network)
        ? undefined
        : 'From 29th April 2023, X (Twitter) no longer provides post stats.';
    case SocialNetworksEnum.google:
      return isBeforeDateLimit(post.datePosted, network)
        ? undefined
        : 'From Feb 2023, Google no longer provides post stats.';
    case SocialNetworksEnum.instagram:
      return post.feedType === PostFeedType.STORY
        ? 'We do not currently provide stats for Instagram stories.'
        : isInstagramPersonal ||
          (context === PostScheduleViewContext.LIBRARY &&
            !post.iterationAggregations?.actualPublishMethods?.some(m => m === PostPublishMethod.DIRECT)) ||
          (context !== PostScheduleViewContext.LIBRARY && post.actualPublishMethod === PostPublishMethod.MANUAL)
        ? 'Post stats are not available. You were sent a notification to publish this post manually.'
        : undefined;
    default:
      return undefined;
  }
};

const showInstagramStatistics = (
  post: PostItemFragment,
  feedType: PostFeedType,
  hasPreviousPosts: boolean,
  context?: PostScheduleViewContext,
  searchPast?: boolean
) => {
  // If we have no previous posts, we never show stats
  if (!hasPreviousPosts) return false;

  const isInstagramPersonalProfile =
    post.socialProfile.__typename === 'InstagramSocialProfile' && post.socialProfile.type === 'personal';

  const hasDirectPublished =
    hasPreviousPosts &&
    !isInstagramPersonalProfile &&
    (((searchPast || context === PostScheduleViewContext.LIBRARY) &&
      post.iterationAggregations?.actualPublishMethods?.some(m => m === PostPublishMethod.DIRECT)) ||
      (context !== PostScheduleViewContext.LIBRARY && post.actualPublishMethod === PostPublishMethod.DIRECT));

  return hasDirectPublished && feedType !== PostFeedType.STORY;
};

const showTwitterStatistics = (post: PostItemFragment, hasPreviousPosts: boolean, date: string | string[]) => {
  // If we have no previous posts, we never show stats
  if (!hasPreviousPosts) return false;

  const validPastPost = post.publishedUtcDateTimes.some(dt => isBeforeDateLimit(dt, 'twitter'));

  return (hasPreviousPosts && isBeforeDateLimit(date, 'twitter')) || validPastPost;
};

const showGoogleStatistics = (post: PostItemFragment, hasPreviousPosts: boolean, date: string | string[]) => {
  // If we have no previous posts, we never show stats
  if (!hasPreviousPosts) return false;

  const validPastPost = post.publishedUtcDateTimes.some(dt => isBeforeDateLimit(dt, 'google'));

  return (hasPreviousPosts && isBeforeDateLimit(date, 'google')) || validPastPost;
};

// X/Twitter (for posts published after 29th April 2023): “From 29th April 2023, X (Twitter) no longer provides post stats.”
// Google Business Profile (for posts published starting Feb 2023): “From Feb 2023, Google no longer provides post stats.”
// Instagram Stories (for all IG Stories): “We do not currently provide stats for Instagram stories.”
// Instagram manual published (for all manually published posts): “Post stats are not available. You were sent a reminder to publish this post manually.”
const showStatistics = (
  post: PostItemFragment,
  network: SocialNetworksEnum | null,
  hasPreviousPosts: boolean,
  context?: PostScheduleViewContext,
  searchPast?: boolean
) => {
  // If we ever have a generic, we never want to give access to stats because they are always manually posted
  if (post.socialProfile.__typename === 'GenericSocialProfile') return false;

  // Determine if any previous published post meets the requirements
  // For Library we look under publishedUtcDateTimes for the oldest published date, but if no dates, we look at datePosted.
  // For Queue we look specifically and only at datePosted
  const dateToReference =
    context === PostScheduleViewContext.LIBRARY && post.publishedUtcDateTimes.length
      ? post.publishedUtcDateTimes
      : post.datePosted;

  switch (network) {
    case SocialNetworksEnum.twitter:
      return showTwitterStatistics(post, hasPreviousPosts, dateToReference);
    case SocialNetworksEnum.instagram:
      return showInstagramStatistics(post, post.feedType, hasPreviousPosts, context, searchPast);
    case SocialNetworksEnum.google:
      return showGoogleStatistics(post, hasPreviousPosts, dateToReference);
    default:
      return hasPreviousPosts;
  }
};

const isInstagramSocialProfile = (obj: unknown): obj is InstagramSocialProfile => {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'InstagramSocialProfile'
  );
};

export const statisticsCondition = (
  post: PostItemFragment,
  context?: PostScheduleViewContext,
  isAggratedTable?: boolean,
  searchPast?: boolean
): statisticsConditionType => {
  const network = profilesHelpers.getSocialNetworkFromTypename(post.socialProfile.__typename);
  const isInstagram = network === SocialNetworksEnum.instagram;

  // We define here so we can also pass this value back to the caller
  const hasPreviousPosts =
    !!post.prevPostedCount || !!post.iterationAggregations?.countTimesPosted || !!post.publishedStats;

  const show = isAggratedTable || showStatistics(post, network, hasPreviousPosts, context, searchPast);
  const message = hasPreviousPosts && !isAggratedTable ? statisticsMessaging(post, network, context) : undefined;

  return {
    show,
    isInstagram,
    isTwitter: network === SocialNetworksEnum.twitter,
    hasPreviousPosts,
    network,
    message
  };
};

const getPinterestRelevantStats = (): statDetails[] => [
  // { key: 'REACTIONS', statLabel: PostQueueStatLabelsEnum.likes, isDisabled: false },
  { stat: PostQueueStatLabelsEnum.likes, isDisabled: false },
  { stat: PostQueueStatLabelsEnum.comments, isDisabled: false }
];

const getTikTokRelevantStats = (): statDetails[] => [
  { stat: PostQueueStatLabelsEnum.likes, isDisabled: false },
  { stat: PostQueueStatLabelsEnum.comments, isDisabled: false },
  { stat: PostQueueStatLabelsEnum.share, isDisabled: false },
  { stat: PostQueueStatLabelsEnum.impressions, isDisabled: false },
  { stat: PostQueueStatLabelsEnum.videoViews, isDisabled: false }
];

const getInstagramRelevantStats = (
  post: PostItemFragment,
  context?: PostScheduleViewContext,
  isAggratedTable?: boolean
): statDetails[] => {
  const isInstagramPersonalProfile =
    isInstagramSocialProfile(post.socialProfile) && post.socialProfile.type === 'personal';

  if (
    post.feedType === PostFeedType.STORY ||
    (!isAggratedTable &&
      ((context === PostScheduleViewContext.LIBRARY &&
        !post.iterationAggregations?.actualPublishMethods?.includes(PostPublishMethod.DIRECT)) ||
        (context !== PostScheduleViewContext.LIBRARY && post.actualPublishMethod === PostPublishMethod.MANUAL)))
  ) {
    return [];
  }

  const apiType = (post.socialProfile as InstagramSocialProfile)?.apiType ?? InstagramApiType.NONE;
  if (apiType === InstagramApiType.NONE) return [];

  if (apiType === InstagramApiType.LEGACY || apiType === InstagramApiType.BASIC_DISPLAY) {
    return [
      { stat: PostQueueStatLabelsEnum.likes, isDisabled: false },
      { stat: PostQueueStatLabelsEnum.comments, isDisabled: false }
    ];
  }

  const isDisabled = isInstagramPersonalProfile || apiType === InstagramApiType.INSTAGRAM_GRAPH;
  const isBusinessWithViews =
    !!post.videos.length &&
    apiType === InstagramApiType.FACEBOOK_GRAPH &&
    isBeforeDateLimit(post.datePosted, 'instagram');

  return [
    { stat: PostQueueStatLabelsEnum.likes, isDisabled: false },
    { stat: PostQueueStatLabelsEnum.comments, isDisabled: false },
    { stat: PostQueueStatLabelsEnum.impressions, isDisabled },
    { stat: PostQueueStatLabelsEnum.share, isDisabled },
    ...(isBusinessWithViews ? [{ stat: PostQueueStatLabelsEnum.videoViews, isDisabled: false }] : [])
  ];
};

const getGoogleRelevantStats = (post: PostItemFragment): statDetails[] => {
  return post.publishedUtcDateTimes.some(dt => isBeforeDateLimit(dt, 'google'))
    ? [
        { stat: PostQueueStatLabelsEnum.impressions, isDisabled: false },
        { stat: PostQueueStatLabelsEnum.clicks, isDisabled: false }
      ]
    : [];
};

const getFacebookRelevantStats = (post: PostItemFragment): statDetails[] => [
  { stat: PostQueueStatLabelsEnum.likes, isDisabled: false },
  { stat: PostQueueStatLabelsEnum.comments, isDisabled: false },
  { stat: PostQueueStatLabelsEnum.share, isDisabled: false },
  { stat: PostQueueStatLabelsEnum.impressions, isDisabled: false },
  ...(post.videos.length ? [{ stat: PostQueueStatLabelsEnum.videoViews, isDisabled: false }] : [])
];

const getLinkedInRelevantStats = (post: PostItemFragment): statDetails[] => {
  return isLinkedInSocialProfile(post.socialProfile) && post.socialProfile.type === 'company'
    ? [
        { stat: PostQueueStatLabelsEnum.likes, isDisabled: false },
        { stat: PostQueueStatLabelsEnum.comments, isDisabled: false },
        { stat: PostQueueStatLabelsEnum.share, isDisabled: false },
        { stat: PostQueueStatLabelsEnum.impressions, isDisabled: false },
        ...(post.link?.id ? [{ stat: PostQueueStatLabelsEnum.clicks, isDisabled: false }] : [])
      ]
    : [
        { stat: PostQueueStatLabelsEnum.likes, isDisabled: false },
        { stat: PostQueueStatLabelsEnum.comments, isDisabled: false }
      ];
};

const getTwitterRelevantStats = (post: PostItemFragment): statDetails[] => {
  return post.publishedUtcDateTimes.some(dt => isBeforeDateLimit(dt, 'twitter'))
    ? [
        { stat: PostQueueStatLabelsEnum.likes, isDisabled: false },
        { stat: PostQueueStatLabelsEnum.share, isDisabled: false }
      ]
    : [];
};

const hasShowBoostCriteria = (post: PostItemFragment): boolean => {
  return (
    !!post.isEvergreen && (post.iterationAggregations?.countTimesPosted ?? 0) > 1 && !!post.iterationAggregations?.boost
  );
};

const getRelevantStatsByNetwork = (
  network: SocialNetworksEnum,
  post: PostItemFragment,
  context?: PostScheduleViewContext,
  isAggratedTable?: boolean
): statDetails[] => {
  switch (network) {
    case SocialNetworksEnum.instagram:
      return getInstagramRelevantStats(post, context, isAggratedTable);
    case SocialNetworksEnum.google:
      return getGoogleRelevantStats(post);
    case SocialNetworksEnum.facebook:
      return getFacebookRelevantStats(post);
    case SocialNetworksEnum.linkedin:
      return getLinkedInRelevantStats(post);
    case SocialNetworksEnum.twitter:
      return getTwitterRelevantStats(post);
    case SocialNetworksEnum.pinterest:
      return getPinterestRelevantStats();
    case SocialNetworksEnum.tiktok:
      return getTikTokRelevantStats();
    default:
      return [];
  }
};

export const getRelevantStats = (
  network: SocialNetworksEnum,
  post: PostItemFragment,
  context?: PostScheduleViewContext,
  isAggratedTable?: boolean
): statDetails[] => {
  // set the fixed order
  const orderedStats = [
    PostQueueStatLabelsEnum.likes,
    PostQueueStatLabelsEnum.comments,
    PostQueueStatLabelsEnum.share,
    PostQueueStatLabelsEnum.impressions,
    PostQueueStatLabelsEnum.clicks,
    PostQueueStatLabelsEnum.videoViews,
    PostQueueStatLabelsEnum.boost
  ];

  let relevantStats = getRelevantStatsByNetwork(network, post, context, isAggratedTable);

  if (context === PostScheduleViewContext.LIBRARY && hasShowBoostCriteria(post)) {
    relevantStats.push({ stat: PostQueueStatLabelsEnum.boost, isDisabled: false });
  }

  // ensures static order sequence, remove any undefined, make TS happy by not returning undefined
  return orderedStats.map(stat => relevantStats.find(s => s.stat === stat)).filter((s): s is statDetails => !!s);
};
